import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { ADMIN_PAYMENT_METHOD } from "common/components/admin-payment-form-proptypes";
import { fetchEnv } from "common/features/env/env-selectors";
import pathsService from "common/services/paths-service";
import mixpanelService from "common/mixpanel-service";
import pageData from "common/page-data";
import { selectAccount } from "../../account/account-selectors";
import { selectStripeRetailEnabled } from "../../flippers-selector";
import { useSubmitPurchaseMutation } from "../campaigns-api-endpoints";
import PurchaseModal from "../purchase-modal";
import ReceiptModal from "../receipt-modal";
import CampaignGroupPropType from "../campaign-group-prop-type";
import creditCardIconSet from "../../settings/credit-card-icon-set.png";
import creditCardIconSet2x from "../../settings/credit-card-icon-set@2x.png";

const propTypes = {
  campaignGroup: CampaignGroupPropType.isRequired,
  purchaseModalData: PropTypes.shape({
    showPaymentFailNote: PropTypes.bool,
    showUpgradeSell: PropTypes.bool,
    showOrderSummary: PropTypes.bool,
    confirmationButtonText: PropTypes.string,
    buttonColor: PropTypes.string,
    impressionBoost: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      impression_quantity: PropTypes.number,
      cost: PropTypes.number
    }),
    overridePackage: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    formTarget: PropTypes.string,
    applyCouponLink: PropTypes.string,
    hideCouponLine: PropTypes.bool,
    hideExistingCards: PropTypes.bool
  }).isRequired,
  displayPurchaseModal: PropTypes.bool.isRequired,
  setDisplayPurchaseModal: PropTypes.func.isRequired,
  adminPaymentMethod: ADMIN_PAYMENT_METHOD,
  applyCouponCodeCallback: PropTypes.func,
  isVerifyingCoupon: PropTypes.bool,
  couponData: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    status: PropTypes.string,
    partner_id: PropTypes.number,
    discount_cents: PropTypes.number,
    discount_percent: PropTypes.number,
    allow_other_coupons: PropTypes.bool
  }),
  couponErrorData: PropTypes.shape({
    code: PropTypes.arrayOf(PropTypes.string)
  }),
  purchaseSource: PropTypes.string.isRequired,
  purchaseType: PropTypes.string,
  images: PropTypes.shape({
    cards: PropTypes.string,
    cards2x: PropTypes.string,
    amex: PropTypes.string,
    discover: PropTypes.string,
    mastercard: PropTypes.string,
    visa: PropTypes.string
  }),
  onRefetch: PropTypes.func,
  supportsStripeRetail: PropTypes.bool
};

const PurchaseFlowModals = ({
  campaignGroup,
  purchaseModalData,
  displayPurchaseModal,
  setDisplayPurchaseModal,
  adminPaymentMethod = null,
  applyCouponCodeCallback = () => {},
  isVerifyingCoupon = false,
  couponData = {},
  couponErrorData = {},
  purchaseSource,
  purchaseType = null,
  images = { cards: creditCardIconSet, cards2x: creditCardIconSet2x },
  onRefetch = () => {},
  supportsStripeRetail = false
}) => {
  const [displayReceiptModal, setDisplayReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [purchaseErrorData, setPurchaseErrorData] = useState({});

  const account = useSelector(selectAccount);
  const stripePublishableKey = useSelector(fetchEnv("stripePublishableKey"));
  const stripePaymentMethodsConfigId = useSelector(fetchEnv("stripePaymentMethodsConfigId"));
  const stripeRetailEnabled = useSelector(selectStripeRetailEnabled);
  const [submitPurchase, { isLoading: isPurchasing }] = useSubmitPurchaseMutation();

  const buildPurchaseParams = (chargeInfo, campaignPackage, impressionBoost) => ({
    [campaignGroup.campaign_type_snake]: {
      adwerx_payment_details: {
        transaction_id: chargeInfo.transactionId,
        credit_card_number: chargeInfo.creditCardNumber || "",
        credit_card_expiration_month: chargeInfo.expirationMonth || 1,
        credit_card_expiration_year: chargeInfo.expirationYear || new Date().getFullYear(),
        credit_card_cvv: chargeInfo.cvv || "",
        billing_zip: chargeInfo.zipCode || ""
      },
      subscription_plan_id: campaignPackage?.id
    },
    boost_id: impressionBoost?.id,
    admin_payment: chargeInfo.paymentOption === "admin" ? chargeInfo.adminPayment || "" : undefined,
    admin_custom_amount:
      chargeInfo.paymentOption === "admin" ? chargeInfo.adminCustomAmount || "" : undefined,
    admin_billing_group_id:
      chargeInfo.paymentOption === "admin" ? chargeInfo.adminBillingGroupId || "" : undefined,
    admin_renewal_option:
      chargeInfo.paymentOption === "admin" ? chargeInfo.adminRenewalOption || "" : undefined,
    use_site_credit: chargeInfo.useSiteCredit ? "1" : "0",
    payment_option: chargeInfo.paymentOption || "",
    from: purchaseSource,
    coupon: chargeInfo?.coupon
  });

  const postPurchase = (chargeInfo, campaignPackage, impressionBoost, target, stripeFields = {}) =>
    submitPurchase({
      purchaseUrl: target,
      params: {
        ...buildPurchaseParams(chargeInfo, campaignPackage, impressionBoost),
        ...stripeFields
      },
      campaignGroupId: campaignGroup.id
    }).unwrap();

  const handlePurchaseSuccess = response => {
    if (purchaseType) {
      mixpanelService.track("Campaign.Purchased", {
        ...pageData.get("mixpanelData"),
        campaign_id: campaignGroup.campaign_id,
        campaign_type: campaignGroup.campaign_type,
        purchase_type: purchaseType,
        purchase_source: purchaseSource
      });
    }
    setDisplayPurchaseModal(false);
    setReceiptData(response.receipt);
    setDisplayReceiptModal(true);
  };

  const handlePurchaseError = err => {
    setPurchaseErrorData(err.data || { base: ["Unable to complete purchase. Please try again."] });
  };

  const handlePurchaseSubmit = async (
    event,
    chargeInfo,
    campaignPackage,
    impressionBoost,
    target
  ) => {
    event.preventDefault();
    setPurchaseErrorData({});

    try {
      const response = await postPurchase(chargeInfo, campaignPackage, impressionBoost, target);
      handlePurchaseSuccess(response);
    } catch (err) {
      handlePurchaseError(err);
    }
  };

  const handleCloseReceiptModal = () => {
    setDisplayReceiptModal(false);
    onRefetch();
  };

  const stripeRetailActive = supportsStripeRetail && Boolean(stripeRetailEnabled);
  const customerSessionPath = stripeRetailActive
    ? pathsService.build(pathsService.checkoutCustomerSessionPath, { accountId: account.id })
    : null;

  return (
    <>
      {displayPurchaseModal && (
        <PurchaseModal
          display={displayPurchaseModal}
          toggleDisplay={() => setDisplayPurchaseModal(!displayPurchaseModal)}
          purchaseModalData={purchaseModalData}
          errorData={purchaseErrorData}
          campaignGroup={campaignGroup}
          images={images}
          submitCallback={handlePurchaseSubmit}
          isProcessing={isPurchasing}
          showClientAdminPaymentWarning={account.showClientAdminPaymentWarning}
          showInvoicePurchasing={account.showInvoicePurchasing}
          hideCreditCardPurchasing={account.hideCreditCardPurchasing}
          accountholderName={account.name}
          applyCouponCodeCallback={applyCouponCodeCallback}
          couponData={couponData}
          couponErrorData={couponErrorData}
          isVerifyingCoupon={isVerifyingCoupon}
          adminPaymentMethod={adminPaymentMethod}
          stripeRetail={{
            enabled: stripeRetailActive,
            publishableKey: stripePublishableKey,
            paymentMethodsConfigId: stripePaymentMethodsConfigId,
            customerSessionPath,
            postPurchase,
            onComplete: handlePurchaseSuccess,
            onError: handlePurchaseError
          }}
        />
      )}
      {displayReceiptModal && (
        <ReceiptModal
          display={displayReceiptModal}
          toggleDisplay={handleCloseReceiptModal}
          showNotification
          receiptData={receiptData || {}}
        />
      )}
    </>
  );
};

PurchaseFlowModals.propTypes = propTypes;

export default PurchaseFlowModals;
